/**
 * TanStack Query Plugin for Vue 3
 *
 * This plugin provides data fetching and caching capabilities for the new oRPC API v2.
 * It runs alongside Apollo Client during the migration period.
 *
 * @see https://tanstack.com/query/latest/docs/vue/overview
 */
import type { App } from 'vue'

import { QueryClient, VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query'

/**
 * Global QueryClient instance
 * Used for cache management and prefetching
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 1000 * 60 * 5,
      // Cache data is kept for 30 minutes after all subscribers unmount
      gcTime: 1000 * 60 * 30,
      // Only retry once on failure
      retry: 1,
      // Don't refetch on window focus (matches current Apollo behavior)
      refetchOnWindowFocus: false,
      // Don't refetch on mount if data exists
      refetchOnMount: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
})

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClient,
}

/**
 * Setup TanStack Query plugin for Vue app
 * Call this in main.ts: setupQuery(app)
 */
export function setupQuery(app: App) {
  app.use(VueQueryPlugin, vueQueryPluginOptions)
}
